import React, { useEffect, useState } from "react";
import useAuthUser from "../hooks/useAuthUser";
import { getPlayerPortfolio } from "../utils/portfolioUtils";

export default function PlayerPortfolioTab({ playerName, portfolio: initialPortfolio, profile }) {
  const authUser = useAuthUser();
  const token = authUser?.token;
  const effectiveName = playerName || profile?.userDisplayName;

  const [stocks, setStocks] = useState(initialPortfolio || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "value", direction: "desc" });

  useEffect(() => {
    if (!effectiveName || !token) return;

    async function fetchPortfolio() {
      try {
        setLoading(true);
        setError("");
        const data = await getPlayerPortfolio(effectiveName, token);
        setStocks(data?.companyHoldings || []);
      } catch (err) {
        console.error("Portfolio Fetch Error:", err);
        setError("Failed to load portfolio.");
      } finally {
        setLoading(false);
      }
    }

    fetchPortfolio();
  }, [effectiveName, token]);

  const fmtMoney = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedStocks = [...stocks].sort((a, b) => {
    let valA = a[sortConfig.key];
    let valB = b[sortConfig.key];
    if (typeof valA === "string") valA = valA.toUpperCase();
    if (typeof valB === "string") valB = valB.toUpperCase();
    if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
    if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  if (loading) return <p style={statusStyle} className="blink">LOADING PORTFOLIO...</p>;
  if (error) return <p style={{ ...statusStyle, color: "#ff7b72" }}>{error}</p>;

  const portfolioValue = Number(profile?.userPortfolioValue ?? 0);
  const usd = Number(profile?.userMoney ?? 0);

  const summaryRows = [
    { label: "TOTAL NET WORTH", value: portfolioValue, color: "#50e3c2", bold: true },
    { label: "TOTAL HOLDINGS", value: portfolioValue - usd, color: "#eee" },
    { label: "CASH BALANCE", value: usd, color: "#eee" },
    { label: "FOREX HOLDINGS", value: profile?.userForex ?? 0 },
    { label: "STOCK EQUITY", value: profile?.userStocks ?? 0 },
    { label: "CRYPTO ASSETS", value: profile?.userCrypto ?? 0 },
    { label: "GOLD RESERVES", value: profile?.userGoldValue ?? 0 },
    { label: "BOND HOLDINGS", value: profile?.userBonds ?? 0 },
    { label: "FUTURES POS.", value: profile?.userFutures ?? 0 },
  ];

  return (
    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.7rem", color: "#eee" }}>

      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "1.5rem" }}>
        <tbody>
          {summaryRows.map((row, idx) => (
            <tr key={row.label} style={{ background: idx % 2 === 0 ? "rgba(255,255,255,0.02)" : "transparent" }}>
              <th style={{ 
                textAlign: "left", 
                padding: "4px 8px", 
                color: row.color || "#888", 
                fontWeight: row.bold ? "bold" : "normal",
                borderBottom: "1px solid rgba(80, 227, 194, 0.1)"
              }}>
                {row.label}
              </th>
              <td style={{ 
                textAlign: "right", 
                padding: "4px 8px", 
                color: row.color || "#eee",
                borderBottom: "1px solid rgba(80, 227, 194, 0.1)",
                fontVariantNumeric: "tabular-nums"
              }}>
                {fmtMoney(row.value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={sectionHeaderStyle}>HOLDINGS</div>
      {stocks.length === 0 ? (
        <p style={emptyStyle}>No holdings found.</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "1.5rem" }}>
          <thead>
            <tr style={theadStyle}>
              {["ticker", "amount", "value"].map((key) => (
                <th 
                  key={key} 
                  onClick={() => handleSort(key)}
                  style={{ cursor: "pointer", padding: "8px", textTransform: "uppercase" }}
                >
                  {key === 'amount' ? 'volume' : key}
                  {sortConfig.key === key ? (sortConfig.direction === "asc" ? " ◢" : " ◣") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedStocks.map((holding, idx) => (
              <tr key={idx} style={rowStyle}>
                <td style={{ padding: "8px", fontWeight: "bold", color: "#50e3c2" }}>{holding.ticker}</td>
                <td style={{ padding: "8px", color: "#888" }}>{Number(holding.amount || 0).toLocaleString()}</td>
                <td style={{ padding: "8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {fmtMoney(holding.value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// --- Styles ---
const statusStyle = { 
  fontFamily: "'JetBrains Mono', monospace", 
  fontSize: "0.75rem", 
  color: "#50e3c2", 
  padding: "10px" 
};

const sectionHeaderStyle = { 
  padding: "4px 8px", 
  color: "#50e3c2", 
  fontWeight: "bold", 
  borderBottom: "1px solid #50e3c2", 
  marginBottom: "8px",
  textTransform: "uppercase"
};

const theadStyle = { 
  color: "#888", 
  textAlign: "left", 
  fontSize: "0.6rem" 
};

const rowStyle = { 
  borderBottom: "1px solid rgba(255,255,255,0.05)" 
};

const emptyStyle = { 
  padding: "10px", 
  color: "#555", 
  fontSize: "0.6rem" 
};